import CalendarConfig from '../consts/CalendarConfig';
import { DOMAIN_URL } from '../consts/DomainConfig';

/**
 * Utilitário para processar placeholders em templates de mensagens
 */
const MessageProcessor = {
    /**
     * Processa placeholders em uma mensagem baseado nos dados do agendamento
     * @param {string} template - Template da mensagem com placeholders
     * @param {Object} appointmentData - Dados do agendamento
     * @param {Object} patientData - Dados do paciente
     * @param {Object} resourceData - Dados do profissional/recurso
     * @param {Object} procedureData - Dados do procedimento
     * @param {string} [companyName] - Nome da empresa (cabeçalho "*{company_name}*" dos modelos)
     * @returns {string} Mensagem processada sem placeholders
     */
    processMessage: (template, appointmentData, patientData, resourceData, procedureData, companyName = '') => {
        if (!template) return '';
        
        let message = template;
        
        // Processar dados de data/hora
        const yyyymmdd = CalendarConfig.GetAAAAMMDDArray(appointmentData.date);
        const monthDay = yyyymmdd[2];
        const monthName = CalendarConfig.Months[yyyymmdd[1] - 1];
        const time = CalendarConfig.GetTime(appointmentData.date);
        
        // Processar saudação baseada no horário atual
        const currentHour = new Date().getHours();
        let greetings = "";
        if (currentHour < 12) {
            greetings = "Bom dia";
        } else if (currentHour < 18) {
            greetings = "Boa tarde";
        } else {
            greetings = "Boa noite";
        }
        
        // Processar "quando" (hoje/amanhã)
        const today = new Date();
        const appointmentDate = new Date(appointmentData.date);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        let when = "";
        if (appointmentDate.toDateString() === today.toDateString()) {
            when = "hoje";
        } else if (appointmentDate.toDateString() === tomorrow.toDateString()) {
            when = "amanhã";
        }
        
        // URL de confirmação (legado — mantido para templates antigos)
        const urlToConfirm = DOMAIN_URL + "api/confirm_appointment.php/?id=" + appointmentData.id;

        // Link de confirmação público (V2) — substituído pelo backend quando disponível;
        // aqui usamos o legado como fallback para o preview do seletor de template.
        const confirmationLink = appointmentData.confirmation_link || urlToConfirm;
        
        // Substituir placeholders
        message = message.replaceAll("{company_name}", companyName || '');
        message = message.replaceAll("{greetings}", greetings);
        message = message.replaceAll("{name}", patientData.name ? patientData.name.split(' ')[0] : '');
        message = message.replaceAll("{procedure}", procedureData ? (typeof procedureData === 'string' ? procedureData : procedureData.Label) : '');
        message = message.replaceAll("{doctor_name}", resourceData ? (typeof resourceData === 'string' ? resourceData : resourceData.Label) : '');
        message = message.replaceAll("{when}", when);
        message = message.replaceAll("{day}", monthDay);
        message = message.replaceAll("{month}", monthName);
        message = message.replaceAll("{time}", time);
        message = message.replaceAll("{url}", urlToConfirm);
        message = message.replaceAll("{confirmation_link}", confirmationLink);
        
        return message;
    },

    /**
     * Processa múltiplas mensagens (array de templates)
     * @param {Array} templates - Array de templates
     * @param {Object} appointmentData - Dados do agendamento
     * @param {Object} patientData - Dados do paciente
     * @param {Object} resourceData - Dados do profissional/recurso
     * @param {Object} procedureData - Dados do procedimento
     * @param {string} [companyName] - Nome da empresa
     * @returns {Array} Array de mensagens processadas
     */
    processMessages: (templates, appointmentData, patientData, resourceData, procedureData, companyName = '') => {
        if (!templates || !Array.isArray(templates)) return [];

        return templates.map(template =>
            MessageProcessor.processMessage(template, appointmentData, patientData, resourceData, procedureData, companyName)
        );
    },

    /**
     * Gera dados de exemplo para preview quando dados reais não estão disponíveis
     * @returns {Object} Dados de exemplo
     */
    getExampleData: () => ({
        appointmentData: {
            id: '123',
            date: new Date().toISOString()
        },
        patientData: {
            name: 'Maria Silva'
        },
        resourceData: {
            Label: 'Dr. João Santos'
        },
        procedureData: {
            Label: 'Consulta'
        },
        companyName: 'Empresa Exemplo'
    })
};

export default MessageProcessor;